import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { Check, ChevronLeft, Copy, Loader2, ShieldCheck, ShieldOff } from 'lucide-react'
import {
	EmailAuthProvider,
	TotpMultiFactorGenerator,
	TotpSecret,
	multiFactor,
	reauthenticateWithCredential
} from 'firebase/auth'
import QRCode from 'qrcode'
import { Button } from '@/components/ui/button'
import { FloatingInput } from '@/components/ui/floating-input'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { auth } from '@/constants'
import { apiFetch } from '@/utils'
import { FEATURES } from '@/lib/config'

type EnrollStep = 'idle' | 'reauth' | 'scan' | 'verify'
type DisableStep = 'idle' | 'reauth'

type MfaStatusFactor = {
	enrollmentId: string
	displayName?: string | null
	factorType?: string | null
	enrolledAt?: string | null
}

const TOTP_ISSUER = 'Secrets Hideaways'
const TOTP_FACTOR_NAME = 'Authenticator app'

const friendlyAuthError = (error: unknown, fallback: string): string => {
	const code = (error as { code?: string })?.code
	switch (code) {
		case 'auth/wrong-password':
		case 'auth/invalid-credential':
		case 'auth/invalid-login-credentials':
			return 'Incorrect password. Please try again.'
		case 'auth/too-many-requests':
			return 'Too many attempts. Please wait a moment and try again.'
		case 'auth/invalid-verification-code':
			return 'That code didn’t match. Double-check your authenticator app and try again.'
		case 'auth/requires-recent-login':
			return 'For your security, please re-enter your password.'
		case 'auth/unverified-email':
			return 'Verify your email address before turning on two-factor authentication.'
		default:
			return (error as { message?: string })?.message || fallback
	}
}

export default function TwoFactorAuthPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()

	// MFA enrollment is gated on Firebase Blaze; render a "temporarily disabled"
	// panel for anyone hitting a stale bookmark or deep link until billing is
	// turned on and FEATURES.MFA_ENABLED flips to true.
	if (!FEATURES.MFA_ENABLED) {
		return (
			<div className="min-h-[100dvh] bg-[#18181B] text-white flex flex-col">
				<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
					<div
						role="banner"
						className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
					>
						<button
							type="button"
							aria-label="Go back"
							onClick={() => navigate('/account')}
							className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
						</button>
						<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
							Two-Factor Authentication
						</h1>
					</div>
					<div className="flex-1 flex items-start justify-center px-5 py-8">
						<div className="w-full max-w-sm space-y-4">
							<div className="bg-[#212124] rounded-2xl p-5 flex items-start gap-3">
								<ShieldOff className="h-6 w-6 text-[#BABABB] shrink-0 mt-0.5" />
								<div className="space-y-1">
									<p className="font-figtree text-sm font-semibold text-white">
										Two-factor authentication is temporarily unavailable
									</p>
									<p className="font-figtree text-xs text-[#BABABB] leading-5">
										We're enabling authenticator-app support shortly. Your account is still protected by your password in the meantime.
									</p>
								</div>
							</div>
							<Button
								onClick={() => navigate('/account')}
								className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
							>
								Back to account
							</Button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const [enrolledFactors, setEnrolledFactors] = useState<MfaStatusFactor[]>([])
	const [factorsLoaded, setFactorsLoaded] = useState(false)

	const [enrollStep, setEnrollStep] = useState<EnrollStep>('idle')
	const [enrollPassword, setEnrollPassword] = useState('')
	const [enrollError, setEnrollError] = useState('')
	const [enrollBusy, setEnrollBusy] = useState(false)
	const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null)
	const [qrDataUrl, setQrDataUrl] = useState('')
	const [verifyCode, setVerifyCode] = useState('')
	const [secretCopied, setSecretCopied] = useState(false)

	const [disableStep, setDisableStep] = useState<DisableStep>('idle')
	const [disablePassword, setDisablePassword] = useState('')
	const [disableError, setDisableError] = useState('')
	const [disableBusy, setDisableBusy] = useState(false)
	const [factorToRemove, setFactorToRemove] = useState<MfaStatusFactor | null>(null)

	const totpFactor = useMemo(
		() => enrolledFactors.find((factor) => factor.factorType === 'totp') || null,
		[enrolledFactors]
	)
	const isEnabled = !!totpFactor

	const refreshFactors = async () => {
		if (!auth.currentUser) {
			setEnrolledFactors([])
			setFactorsLoaded(true)
			return
		}
		try {
			const response = await apiFetch('/v1/users/mfa/status', {
				method: 'GET',
				withCredentials: true
			})
			const result = await response.json().catch(() => null)
			if (!response.ok) {
				throw new Error(result?.error?.message || 'Failed to load two-factor status')
			}
			const factors: MfaStatusFactor[] = Array.isArray(result?.data?.factors) ? result.data.factors : []
			setEnrolledFactors(factors)
		} catch (error) {
			console.error('Failed to load MFA status', error)
			toast({
				title: 'Couldn’t load two-factor status',
				description: error instanceof Error ? error.message : 'Please try again.',
				variant: 'destructive'
			})
			setEnrolledFactors([])
		} finally {
			setFactorsLoaded(true)
		}
	}

	useEffect(() => {
		refreshFactors()
	}, [])

	const resetEnrollState = () => {
		setEnrollStep('idle')
		setEnrollPassword('')
		setEnrollError('')
		setTotpSecret(null)
		setQrDataUrl('')
		setVerifyCode('')
		setSecretCopied(false)
	}

	const resetDisableState = () => {
		setDisableStep('idle')
		setDisablePassword('')
		setDisableError('')
		setFactorToRemove(null)
	}

	const beginEnrollment = () => {
		setEnrollError('')
		setEnrollPassword('')
		setEnrollStep('reauth')
	}

	const beginDisable = (factor: MfaStatusFactor) => {
		setDisableError('')
		setDisablePassword('')
		setFactorToRemove(factor)
		setDisableStep('reauth')
	}

	const reauthenticate = async (password: string) => {
		const user = auth.currentUser
		if (!user || !user.email) {
			throw new Error('You need to be signed in to manage two-factor authentication.')
		}
		const credential = EmailAuthProvider.credential(user.email, password)
		await reauthenticateWithCredential(user, credential)
	}

	const startTotpSetup = async () => {
		setEnrollBusy(true)
		setEnrollError('')
		try {
			await reauthenticate(enrollPassword)

			const user = auth.currentUser!
			const mfa = multiFactor(user)
			const session = await mfa.getSession()
			const secret = await TotpMultiFactorGenerator.generateSecret(session)
			const otpAuthUrl = secret.generateQrCodeUrl(user.email ?? 'user', TOTP_ISSUER)
			const dataUrl = await QRCode.toDataURL(otpAuthUrl, { width: 220, margin: 1 })

			setTotpSecret(secret)
			setQrDataUrl(dataUrl)
			setEnrollPassword('')
			setEnrollStep('scan')
		} catch (error) {
			console.error('Failed to start TOTP enrollment', error)
			setEnrollError(friendlyAuthError(error, 'Could not start setup. Please try again.'))
		} finally {
			setEnrollBusy(false)
		}
	}

	const confirmTotpEnrollment = async () => {
		if (!totpSecret) return
		const code = verifyCode.replace(/\s+/g, '')
		if (code.length !== 6) {
			setEnrollError('Enter the 6-digit code from your authenticator app.')
			return
		}
		setEnrollBusy(true)
		setEnrollError('')
		try {
			const user = auth.currentUser
			if (!user) throw new Error('You need to be signed in.')
			const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code)
			await multiFactor(user).enroll(assertion, TOTP_FACTOR_NAME)
			toast({
				title: 'Two-factor authentication enabled',
				description: 'You’ll be asked for a code from your authenticator app next time you sign in.'
			})
			resetEnrollState()
			await refreshFactors()
		} catch (error) {
			console.error('TOTP enrollment failed', error)
			setEnrollError(friendlyAuthError(error, 'Verification failed. Please try again.'))
		} finally {
			setEnrollBusy(false)
		}
	}

	const confirmDisable = async () => {
		if (!factorToRemove) return
		setDisableBusy(true)
		setDisableError('')
		try {
			await reauthenticate(disablePassword)
			const user = auth.currentUser
			if (!user) throw new Error('You need to be signed in.')
			await multiFactor(user).unenroll(factorToRemove.enrollmentId)
			toast({
				title: 'Two-factor authentication disabled',
				description: 'You can re-enable it at any time from this screen.'
			})
			resetDisableState()
			await refreshFactors()
		} catch (error) {
			console.error('Failed to disable TOTP', error)
			setDisableError(friendlyAuthError(error, 'Could not disable two-factor authentication.'))
		} finally {
			setDisableBusy(false)
		}
	}

	const copySecret = async () => {
		if (!totpSecret) return
		try {
			await navigator.clipboard.writeText(totpSecret.secretKey)
			setSecretCopied(true)
			setTimeout(() => setSecretCopied(false), 2000)
		} catch (error) {
			console.warn('Clipboard copy failed', error)
		}
	}

	return (
		<div className="min-h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Two-Factor Authentication
					</h1>
				</div>

				<div className="flex-1 flex items-start justify-center px-5 py-8">
					<div className="w-full max-w-sm space-y-6">
						<div className="bg-[#212124] rounded-2xl p-5 flex items-start gap-3">
							{isEnabled ? (
								<ShieldCheck className="h-6 w-6 text-[#2ecc71] shrink-0 mt-0.5" />
							) : (
								<ShieldOff className="h-6 w-6 text-[#BABABB] shrink-0 mt-0.5" />
							)}
							<div className="space-y-1">
								<p className="font-figtree text-sm font-semibold text-white">
									{isEnabled ? 'Two-factor authentication is on' : 'Two-factor authentication is off'}
								</p>
								<p className="font-figtree text-xs text-[#BABABB] leading-5">
									{isEnabled
										? 'Codes from your authenticator app are required when signing in.'
										: 'Protect your account with a code from an authenticator app (Google Authenticator, Authy, 1Password, etc.) in addition to your password.'}
								</p>
							</div>
						</div>

						{!factorsLoaded ? (
							<div className="flex items-center justify-center py-6">
								<Loader2 className="h-5 w-5 animate-spin text-[#BABABB]" />
							</div>
						) : isEnabled ? (
							<div className="space-y-3">
								<div className="bg-[#212124] rounded-2xl p-4">
									<p className="font-figtree text-sm text-white font-medium">
										{totpFactor?.displayName || TOTP_FACTOR_NAME}
									</p>
									{totpFactor?.enrolledAt && (
										<p className="font-figtree text-xs text-[#BABABB] mt-1">
											Added {new Date(totpFactor.enrolledAt).toLocaleDateString()}
										</p>
									)}
								</div>
								<Button
									onClick={() => totpFactor && beginDisable(totpFactor)}
									className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
								>
									Disable two-factor authentication
								</Button>
							</div>
						) : (
							<Button
								onClick={beginEnrollment}
								className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
							>
								Set up authenticator app
							</Button>
						)}
					</div>
				</div>
			</div>

			{/* Enroll: reauth */}
			<AlertDialog
				open={enrollStep === 'reauth'}
				onOpenChange={(open) => {
					if (!open && !enrollBusy) resetEnrollState()
				}}
			>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Confirm your password
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							Re-enter your password to set up two-factor authentication.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="my-2">
						<FloatingInput
							label="Password"
							type="password"
							autoFocus
							autoComplete="current-password"
							value={enrollPassword}
							onChange={(e) => {
								setEnrollPassword(e.target.value)
								setEnrollError('')
							}}
						/>
						{enrollError && (
							<p className="text-sm text-[#EF4444] mt-2 text-center font-figtree">{enrollError}</p>
						)}
					</div>

					<AlertDialogFooter className="flex flex-col gap-3 !sm:flex-col !sm:justify-center !sm:space-x-0 items-center">
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								if (!enrollPassword.trim()) {
									setEnrollError('Please enter your password.')
									return
								}
								startTotpSetup()
							}}
							disabled={enrollBusy}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{enrollBusy ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Verifying...
								</>
							) : (
								'Continue'
							)}
						</AlertDialogAction>
						<AlertDialogCancel
							onClick={resetEnrollState}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Enroll: scan + verify */}
			<AlertDialog
				open={enrollStep === 'scan'}
				onOpenChange={(open) => {
					if (!open && !enrollBusy) resetEnrollState()
				}}
			>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Scan with your authenticator app
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							Add this account in Google Authenticator, Authy, 1Password, or a similar app, then enter the 6-digit code below.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="my-2 space-y-4">
						{qrDataUrl && (
							<div className="flex justify-center">
								<img
									src={qrDataUrl}
									alt="Two-factor authentication QR code"
									className="h-[220px] w-[220px] rounded-lg bg-white p-2"
								/>
							</div>
						)}

						{totpSecret && (
							<button
								type="button"
								onClick={copySecret}
								className="w-full flex items-center justify-between gap-3 bg-[#212124] rounded-lg px-3 py-2 text-left"
							>
								<span className="font-figtree text-xs text-[#BABABB] break-all">
									{totpSecret.secretKey}
								</span>
								<span className="flex items-center gap-1 text-xs text-[#BABABB] shrink-0">
									{secretCopied ? <Check className="h-4 w-4 text-[#2ecc71]" /> : <Copy className="h-4 w-4" />}
									{secretCopied ? 'Copied' : 'Copy'}
								</span>
							</button>
						)}

						<FloatingInput
							label="6-digit code"
							type="text"
							inputMode="numeric"
							autoComplete="one-time-code"
							maxLength={6}
							value={verifyCode}
							onChange={(e) => {
								setVerifyCode(e.target.value.replace(/[^0-9]/g, ''))
								setEnrollError('')
							}}
						/>

						{enrollError && (
							<p className="text-sm text-[#EF4444] text-center font-figtree">{enrollError}</p>
						)}
					</div>

					<AlertDialogFooter className="flex flex-col gap-3 !sm:flex-col !sm:justify-center !sm:space-x-0 items-center">
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								confirmTotpEnrollment()
							}}
							disabled={enrollBusy}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{enrollBusy ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Verifying...
								</>
							) : (
								'Turn on two-factor auth'
							)}
						</AlertDialogAction>
						<AlertDialogCancel
							onClick={resetEnrollState}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Disable: reauth */}
			<AlertDialog
				open={disableStep === 'reauth'}
				onOpenChange={(open) => {
					if (!open && !disableBusy) resetDisableState()
				}}
			>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Disable two-factor authentication?
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							Removing this factor will make your account less secure. Re-enter your password to confirm.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="my-2">
						<FloatingInput
							label="Password"
							type="password"
							autoFocus
							autoComplete="current-password"
							value={disablePassword}
							onChange={(e) => {
								setDisablePassword(e.target.value)
								setDisableError('')
							}}
						/>
						{disableError && (
							<p className="text-sm text-[#EF4444] mt-2 text-center font-figtree">{disableError}</p>
						)}
					</div>

					<AlertDialogFooter className="flex flex-col gap-3 !sm:flex-col !sm:justify-center !sm:space-x-0 items-center">
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								if (!disablePassword.trim()) {
									setDisableError('Please enter your password.')
									return
								}
								confirmDisable()
							}}
							disabled={disableBusy}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{disableBusy ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Disabling...
								</>
							) : (
								'Disable'
							)}
						</AlertDialogAction>
						<AlertDialogCancel
							onClick={resetDisableState}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
